
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDistanceToNow } from 'date-fns';
import { Check, X, User, RefreshCw, Shield } from "lucide-react";
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from '@/utils/adminAudit';
import { toast } from 'sonner';

interface Member {
  id: string;
  full_name: string;
  phone_number: string;
  state: string;
  local_government: string;
  created_at: string;
  email?: string;
  referred_by?: string | null;
  referrer_name?: string | null;
}

interface PendingApprovalsTableProps {
  pendingMembers: Member[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  loading?: boolean;
}

const PendingApprovalsTable = ({ 
  pendingMembers, 
  onApprove, 
  onReject,
  loading = false
}: PendingApprovalsTableProps) => {
  const [membersWithReferrers, setMembersWithReferrers] = useState<Member[]>([]);
  const [loadingReferrers, setLoadingReferrers] = useState<Record<string, boolean>>({});
  const [memberRoles, setMemberRoles] = useState<Record<string, string[]>>({});
  const [processingActions, setProcessingActions] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    setMembersWithReferrers(pendingMembers);
    fetchMemberRoles();
    fetchMissingReferrers();
  }, [pendingMembers]);

  const fetchMemberRoles = async () => {
    try {
      const memberIds = pendingMembers.map(m => m.id);
      if (memberIds.length === 0) return;

      const { data: rolesData, error } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', memberIds);

      if (error) throw error;

      const rolesMap: Record<string, string[]> = {};
      rolesData?.forEach(role => {
        if (!rolesMap[role.user_id]) {
          rolesMap[role.user_id] = [];
        }
        rolesMap[role.user_id].push(role.role);
      });

      setMemberRoles(rolesMap);
    } catch (error) {
      console.error('Error fetching member roles:', error);
    }
  };
  
  const fetchMissingReferrers = async () => {
    const updatedMembers = [...pendingMembers];
    const newLoadingStates: Record<string, boolean> = {};
    
    for (let i = 0; i < updatedMembers.length; i++) {
      const member = updatedMembers[i];
      if (member.referred_by && !member.referrer_name) {
        newLoadingStates[member.id] = true;
        setLoadingReferrers(prev => ({ ...prev, [member.id]: true }));
        
        try {
          const { data, error } = await supabase
            .from('community_members')
            .select('nickname, full_name')
            .eq('id', member.referred_by)
            .single();
            
          if (!error && data) {
            updatedMembers[i] = {
              ...member,
              referrer_name: data.nickname || data.full_name
            };
          }
        } catch (error) {
          console.error('Error fetching referrer details:', error);
        } finally {
          newLoadingStates[member.id] = false;
        }
      }
    }
    
    setMembersWithReferrers(updatedMembers);
    setLoadingReferrers(newLoadingStates);
  };

  const refreshReferrer = async (memberId: string, referrerId: string | null | undefined) => {
    if (!referrerId) return;
    
    setLoadingReferrers(prev => ({ ...prev, [memberId]: true }));
    
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select('nickname, full_name')
        .eq('id', referrerId)
        .single();
        
      if (error) throw error;
      
      if (data) {
        const referrerName = data.nickname || data.full_name;
        setMembersWithReferrers(prev => 
          prev.map(member => 
            member.id === memberId 
              ? { ...member, referrer_name: referrerName } 
              : member
          )
        );
      }
    } catch (error) {
      console.error('Error refreshing referrer info:', error);
    } finally {
      setLoadingReferrers(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleSecureApprove = async (memberId: string) => {
    setProcessingActions(prev => ({ ...prev, [memberId]: true }));
    
    try {
      const member = pendingMembers.find(m => m.id === memberId);
      if (!member) {
        toast.error('Member not found');
        return;
      }

      // Log the approval action first
      await logAdminAction({
        action: 'APPROVE_MEMBER',
        targetTable: 'community_members',
        targetId: memberId,
        oldValues: { approved: false },
        newValues: { approved: true }
      });

      // Then proceed with the approval
      onApprove(memberId);
      
      toast.success(`${member.full_name} has been approved successfully`);
    } catch (error) {
      console.error('Error in secure approve:', error);
      toast.error('Failed to approve member');
    } finally {
      setProcessingActions(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleSecureReject = async (memberId: string) => {
    setProcessingActions(prev => ({ ...prev, [memberId]: true }));
    
    try {
      const member = pendingMembers.find(m => m.id === memberId);
      if (!member) {
        toast.error('Member not found');
        return;
      }

      // Log the rejection action first
      await logAdminAction({
        action: 'REJECT_MEMBER',
        targetTable: 'community_members',
        targetId: memberId,
        oldValues: member,
        newValues: { status: 'rejected' }
      });

      // Then proceed with the rejection
      onReject(memberId);
      
      toast.success(`${member.full_name} has been rejected`);
    } catch (error) {
      console.error('Error in secure reject:', error);
      toast.error('Failed to reject member');
    } finally {
      setProcessingActions(prev => ({ ...prev, [memberId]: false }));
    }
  };
  
  if (loading) {
    return (
      <div className="py-8 text-center">
        <div className="w-8 h-8 border-2 border-omoduduwa-indigo border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <p className="text-gray-500">Loading pending approvals...</p>
      </div>
    );
  }
  
  if (pendingMembers.length === 0) {
    return (
      <div className="py-8 text-center">
        <User className="mx-auto h-12 w-12 text-gray-300 mb-2" />
        <p className="text-gray-500">No pending approvals</p>
      </div>
    );
  }
  
  const displayMembers = membersWithReferrers.length > 0 ? membersWithReferrers : pendingMembers;
  
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Referred By</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayMembers.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-medium">{member.full_name}</TableCell>
              <TableCell>
                {member.phone_number}
                {member.email && <div className="text-xs text-gray-500">{member.email}</div>}
              </TableCell>
              <TableCell>
                {member.state}, {member.local_government}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  {memberRoles[member.id]?.includes('admin') && (
                    <Shield className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-sm">
                    {memberRoles[member.id]?.join(', ') || 'Member'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {member.referrer_name ? (
                  <div className="flex items-center">
                    <span className="text-omoduduwa-terracotta mr-2">{member.referrer_name}</span>
                    {loadingReferrers[member.id] ? (
                      <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                    ) : (
                      <button 
                        onClick={() => refreshReferrer(member.id, member.referred_by)}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Refresh referrer info"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : member.referred_by ? (
                  <div className="flex items-center">
                    <span className="text-gray-400 italic mr-2">Loading referrer...</span>
                    {loadingReferrers[member.id] ? (
                      <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                    ) : (
                      <button 
                        onClick={() => refreshReferrer(member.id, member.referred_by)}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Retry loading referrer"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400">Direct registration</span>
                )}
              </TableCell>
              <TableCell>
                {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-green-500 hover:bg-green-100 text-green-600"
                  onClick={() => handleSecureApprove(member.id)}
                  disabled={processingActions[member.id]}
                >
                  <Check className="w-4 h-4 mr-1" /> 
                  {processingActions[member.id] ? 'Processing...' : 'Approve'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500 hover:bg-red-100 text-red-600"
                  onClick={() => handleSecureReject(member.id)}
                  disabled={processingActions[member.id]}
                >
                  <X className="w-4 h-4 mr-1" /> 
                  {processingActions[member.id] ? 'Processing...' : 'Reject'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PendingApprovalsTable;
